<script>
import { mapState, mapGetters, mapActions } from "vuex";
import PromocodePreview from "@/components/referral-links/PromocodePreview";
import PromocodeCard from "@/components/referral-links/PromocodeCard.vue";
import NotMetConditionsBlock from "@/components/referral-links/NotMetConditionsBlock.vue";
import MainLoader from "@/components/helpers/MainLoader.vue";
import MasterAccountRestriction from "@/components/referral-links/MasterAccountRestriction.vue";
import MainButton from "@/components/helpers/MainButton.vue";

export default {
  name: "ReferralLinksTab",

  components: {
    MainButton,
    MasterAccountRestriction,
    MainLoader,
    PromocodeCard,
    PromocodePreview,
  },

  props: {
    currentCountry: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      restriction: null,
      premiumTariffs: ["vip_d", "vip_gold_d"],
      franchiseTariff: "vip_d",
      referralLinkTariff: ["premium_d", "vip_gold_d"],
      promocodeTariff: ["premium_d", "vip_d", "vip_gold_d"],
      highLevelTariff: ["premium_d", "vip_d", "vip_gold_d"],
      allTariffs: ["business_d", "base_d", "premium_d", "vip_d", "vip_gold_d"],
      tsp: ["partner", "rcpos"],

      countryConfig: {
        KGZ: {
          officeUrl: process.env.VUE_APP_RC_OFFICE_LOCAL_KG,
          payUrl: process.env.VUE_APP_RC_PAY_LOCAL_KG,
          cityUrl: process.env.VUE_APP_RC_CITY_LOCAL_KG,
        },
        UZB: {
          officeUrl: process.env.VUE_APP_RC_OFFICE_LOCAL_UZ,
        },
        default: {
          officeUrl: process.env.VUE_APP_RC_OFFICE_LOCAL_KG,
        },
      },

      dynamicServices: [],
    };
  },

  watch: {
    selectedBinaryAccount: function () {
      this.loadLinks();
    },

    currentCountry: {
      immediate: true,
      handler() {
        this.updateServices();
      },
    },

    selectedPromocode: {
      handler() {
        this.updateServices();
      },
    },
  },

  computed: {
    ...mapState({
      referralLinks: (state) => state.referral.referralLinks,
      selectedBinaryAccount: (state) => state.auth.selectedBinaryAccount,
      selectedPromocode: (state) => state.referral.selectedPromocode,
      user: (state) => state.auth.user,
    }),

    ...mapGetters({
      getReferralLinkByPromocode: "referral/getReferralLinkByPromocode",
    }),

    isShowLinks() {
      return (
        this.selectedBinaryAccount?.position !== "master" ||
        this.user.tariff.code === "business_d"
      );
    },

    currentCountryConfig() {
      return (
        this.countryConfig[this.currentCountry] || this.countryConfig.default
      );
    },

    services() {
      return this.dynamicServices;
    },

    enabledServices() {
      let services = [...this.services];

      if (
        this.selectedBinaryAccount.tariff.code === "vip_d" &&
        this.user.latest_exam?.status !== "passed"
      ) {
        services = services.filter((service) => service.id !== "samo");
      }

      if (
        this.selectedBinaryAccount.position === "master" &&
        this.highLevelTariff.includes(this.selectedBinaryAccount.tariff.code)
      ) {
        if (this.user.latest_exam?.status !== "passed") {
          return services.filter(
            (service) => !this.tsp.includes(service.id) && !!service.link
          );
        }

        return services.filter((service) => !!service.link);
      }

      if (this.showMasterRestriction) {
        return [];
      }

      return services.filter((service) => !!service.link);
    },

    showPromocode() {
      return !!this.enabledServices.length;
    },

    showMasterRestriction() {
      return (
        this.referralLinkTariff.includes(this.user.tariff.code) &&
        this.selectedBinaryAccount.position === "master"
      );
    },

    isShowMainInfo() {
      return (
        this.selectedBinaryAccount &&
        this.user?.tariff?.code !== "smart_c" &&
        this.user?.tariff?.code !== "standard_c"
      );
    },
  },

  methods: {
    ...mapActions({
      loadReferralLinks: "referral/loadReferralLinks",
    }),

    updateServices() {
      this.dynamicServices = [
        {
          id: "distributor",
          label: "RC OFFICE",
          link: `${this.currentCountryConfig.officeUrl}/registration?promocode=${this.selectedPromocode}`,
        },
      ];

      const tariffCode = this.selectedBinaryAccount?.tariff?.code;

      if (
        this.premiumTariffs.includes(tariffCode) &&
        this.user.latest_exam?.status === "passed" &&
        this.currentCountry === "KGZ"
      ) {
        this.dynamicServices.push({
          id: "rccity",
          label: "RC CITY",
          link: `${this.currentCountryConfig.cityUrl}/register-code?code=${this.selectedPromocode}`,
        });
      }

      if (
        this.allTariffs.includes(tariffCode) &&
        this.currentCountry === "KGZ"
      ) {
        this.dynamicServices.push({
          id: "rcpay",
          label: "RC PAY",
          link: `${this.currentCountryConfig.payUrl}/r/${this.selectedPromocode}`,
        });
      }
    },

    loadLinks() {
      this.isLoading = true;
      this.loadReferralLinks(this.selectedBinaryAccount.id).finally(() => {
        this.isLoading = false;
      });
    },

    goToTariffs() {
      this.$router.push({
        name: "Tariffs",
      });
    },

    addCountryConfig(countryCode, config) {
      this.countryConfig[countryCode] = config;

      this.updateServices();
    },
  },

  mounted() {
    if (!this.selectedBinaryAccount) {
      return;
    }

    this.loadLinks();

    this.updateServices();
  },
};
</script>

<template>
  <div class="referral-links">
    <div v-if="isShowMainInfo" class="referral-links__account account">
      <div class="account__wrapper">
        <div
          v-if="showPromocode && !isLoading && isShowLinks"
          class="account__promo-codes"
        >
          <PromocodePreview
            v-for="referralLink in referralLinks"
            :key="referralLink.promocode"
            :promocode="referralLink.promocode"
            :referral-link="referralLink"
          />
        </div>

        <h3>
          <img src="@/assets/icons/referral_links.svg" alt="" /> Реферальные
          ссылки
        </h3>

        <MainLoader v-if="isLoading" />

        <section v-else class="referral-links__services-list">
          <MasterAccountRestriction v-if="!isShowLinks" />

          <template v-else>
            <PromocodeCard
              v-for="service in enabledServices"
              :key="`${currentCountry}-${service.id}`"
              :service="service"
            />
          </template>
        </section>
      </div>
    </div>

    <div v-else class="empty-condition">
      <p>
        Чтобы получить доступ ко всем возможностям , необходим тариф
        <span>Бизнес</span> или выше
      </p>

      <MainButton title="Сменить тариф" color="gold" @click="goToTariffs" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.referral-links {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &__account {
    width: 100%;
    height: 100%;
  }

  &__services-list {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.account {
  &__wrapper {
    display: flex;
    flex-direction: column;
    background-color: $background-white;
    //padding: $space-xxl;

    @media (min-width: 426px) and (max-width: 576px) {
      padding: $space-xl;
    }

    @media (max-width: 425px) {
      padding: $space-xl $space-l;
    }

    @media (max-width: 375px) {
      padding: $space-xl $space-m;
    }

    h3 {
      @include text-1;
      color: $dark-primary;
      margin-bottom: $space-xl;
    }
  }

  &__promo-codes {
    margin-bottom: $space-xl;
  }
}

.empty-condition {
  max-width: 418px;

  > p {
    color: var(--text-dark-primary);
    margin-bottom: 32px;

    @include body-1;

    > span {
      @include body-1-bold;
    }
  }

  > button {
    width: 300px;
  }
}

@media (min-width: 1024px) {
  .referral-links {
    &__services-list {
      gap: 24px;
    }
  }
}

@media (min-width: 1440px) {
  .referral-links {
    &__services-list {
      max-width: 940px;
      grid-template-columns: 1fr 1fr;
    }
  }
}
</style>
